import React from "react";

import {
  formatMoneyFull,
  formatPercent,
} from "../../../utils/formatters";

import ScenarioCoverageManager from "./ScenarioCoverageManager";
import CoverageForm from "./CoverageForm";
import CoverageAdequacyPanel from "./CoverageAdequacyPanel";

export default function CoveragePortfolio({
  coverages = [],
  scenarios = [],
  settings,
  loading,
  mutationLoading,
  error,

  adequacyData,
  adequacyLoading,
  adequacyError,
  onRefreshAdequacy,
  onAnalyzeScenario,

  canAdd = false,
  canChange = false,
  canDelete = false,
  canExecute = false,
  onCreateCoverage,
  onUpdateCoverage,
  onChangeCoverageStatus,
  onDeleteCoverage,
  onLinkScenario,
  onUnlinkScenario,
}) {

  const [
    actionCoverage,
    setActionCoverage,
  ] = React.useState(null);

  const [
    actionError,
    setActionError,
  ] = React.useState(null);

  const [showCreateForm, setShowCreateForm] =
    React.useState(false);

  const platformCurrency =
    settings?.currency || "USD";

  const [selectedCoverageId, setSelectedCoverageId] =
    React.useState(null);

  const selectedCoverage =
    coverages.find(
        (coverage) =>
        coverage.id === selectedCoverageId
    ) || null;

  const [editingCoverage, setEditingCoverage] =
    React.useState(null);

  if (loading) {
    return (
      <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
        Loading coverage portfolio...
      </div>
    );
  }

  if (error && coverages.length === 0) {
    return (
      <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-sm text-red-800">
        Unable to load the coverage portfolio.
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">
            Coverage Portfolio
          </h2>

          <p className="text-sm text-slate-500 mt-1">
            Current insurance and risk-transfer arrangements
            linked to FAIR scenarios.
          </p>
        </div>

        {canAdd && <button
        type="button"
        onClick={() =>
            setShowCreateForm(true)
        }
        className="rounded-lg bg-slate-900 px-4 py-2 text-sm text-white"
        >
        New Coverage
        </button>}

      </div>

      <CoveragePortfolioKpis
        coverages={coverages}
        platformCurrency={platformCurrency}
      />

      <CoverageAdequacyPanel
        data={adequacyData}
        settings={settings}
        loading={adequacyLoading}
        error={adequacyError}
        onRefresh={canExecute ? onRefreshAdequacy : null}
        onAnalyzeScenario={
          canExecute ? onAnalyzeScenario : null
        }
      />

      {coverages.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-6 text-sm text-slate-500">
          No financing coverages have been registered.
        </div>
      ) : (
        <>
          <div className="border-t border-slate-200 pt-6">
            <h2 className="text-lg font-semibold text-slate-900">
              Coverage Administration
            </h2>

            <p className="mt-1 text-sm text-slate-500">
              Manage financing arrangements, lifecycle status and linked FAIR scenarios.
            </p>
          </div>

          <CoverageTable
            coverages={coverages}
            platformCurrency={platformCurrency}
            mutationLoading={mutationLoading}
            onManageScenarios={canChange ? setSelectedCoverageId : null}
            onEditCoverage={canChange ? setEditingCoverage : null}
            onChangeStatus={canChange ? onChangeCoverageStatus : null}
            onDeleteCoverage={canDelete ? setActionCoverage : null}
          />
        </>
      )}

      {selectedCoverage && (
        <ScenarioCoverageManager
          coverage={selectedCoverage}
          scenarios={scenarios}
          loading={mutationLoading}
          onClose={() =>
            setSelectedCoverageId(null)
          }
          onLink={(payload) =>
            onLinkScenario(
              selectedCoverage.id,
              payload
            )
          }
          onUnlink={(scenarioId) =>
            onUnlinkScenario(
              selectedCoverage.id,
              scenarioId
            )
          }
        />
      )}

      {showCreateForm && (
        <CoverageForm
            settings={settings}
            loading={mutationLoading}
            onClose={() =>
            setShowCreateForm(false)
            }
            onSubmit={onCreateCoverage}
        />
      )}

      {editingCoverage && (
        <CoverageForm
          coverage={editingCoverage}
          settings={settings}
          loading={mutationLoading}
          onClose={() =>
            setEditingCoverage(null)
          }
          onSubmit={(payload) =>
            onUpdateCoverage(
              editingCoverage.id,
              payload
            )
          }
        />
      )}

      {actionCoverage && (
        <DeleteCoverageDialog
          coverage={actionCoverage}
          loading={mutationLoading}
          error={actionError}
          onCancel={() => {
            setActionCoverage(null);
            setActionError(null);
          }}
          onConfirm={async () => {
            setActionError(null);

            const result =
              await onDeleteCoverage(
                actionCoverage.id
              );

            if (!result?.success) {
              const messages =
                result?.error?.response?.data
                  ?.errors;

              setActionError(
                Array.isArray(messages)
                  ? messages.join(" ")
                  : "Unable to delete coverage."
              );

              return;
            }

            setActionCoverage(null);
          }}
        />
      )}

    </div>
  );
}

function CoveragePortfolioKpis({
  coverages,
  platformCurrency,
}) {
  const activeCoverages = coverages.filter(
    (coverage) =>
      coverage.status === "ACTIVE"
  );

  const totalPremium = activeCoverages.reduce(
    (total, coverage) =>
      total +
      Number(coverage.annual_premium ?? 0),
    0
  );

  const totalLimits = activeCoverages.reduce(
    (total, coverage) =>
      total +
      Number(coverage.coverage_limit ?? 0),
    0
  );

  const linkedScenarios = new Set(
    coverages.flatMap((coverage) =>
      (coverage.linked_scenarios || []).map(
        (scenario) => scenario.scenario_id
      )
    )
  );

  return (
    <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
      <KpiCard
        title="Active Coverages"
        value={activeCoverages.length}
      />

      <KpiCard
        title="Annual Premium"
        value={formatMoneyFull(
          totalPremium,
          platformCurrency,
          0
        )}
      />

      <KpiCard
        title="Nominal Limits"
        value={formatMoneyFull(
          totalLimits,
          platformCurrency,
          0
        )}
      />

      <KpiCard
        title="Linked Scenarios"
        value={linkedScenarios.size}
      />
    </div>
  );
}

function CoverageTable({
  coverages,
  platformCurrency,
  mutationLoading,
  onManageScenarios,
  onEditCoverage,
  onChangeStatus,
  onDeleteCoverage,
}) {
  return (
    <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
      <table className="min-w-full text-sm">
        <thead>
          <tr className="border-b border-slate-200 text-left text-slate-500">
            <th className="px-4 py-3">Coverage</th>
            <th className="px-4 py-3">Provider</th>
            <th className="px-4 py-3">Status</th>
            <th className="px-4 py-3">Premium</th>
            <th className="px-4 py-3">Deductible</th>
            <th className="px-4 py-3">Limit</th>
            <th className="px-4 py-3">Coinsurance</th>
            <th className="px-4 py-3">Expiration</th>
            <th className="px-4 py-3">Scenarios</th>
            <th className="px-4 py-3">Actions</th>
          </tr>
        </thead>

        <tbody>
          {coverages.map((coverage) => {
            const currency =
              coverage.currency ||
              platformCurrency;

            return (
              <tr
                key={coverage.id}
                className="border-b border-slate-100"
              >
                <td className="px-4 py-3">
                  <p className="font-medium text-slate-900">
                    {coverage.name}
                  </p>

                  <p className="text-xs text-slate-500 mt-1">
                    {formatCoverageType(
                      coverage.coverage_type
                    )}
                  </p>
                </td>

                <td className="px-4 py-3">
                  {coverage.provider || "—"}
                </td>

                <td className="px-4 py-3">
                  <StatusBadge
                    status={coverage.status}
                  />
                </td>

                <td className="px-4 py-3">
                  {formatMoneyFull(
                    coverage.annual_premium,
                    currency
                  )}
                </td>

                <td className="px-4 py-3">
                  {formatMoneyFull(
                    coverage.deductible,
                    currency
                  )}
                </td>

                <td className="px-4 py-3">
                  {formatMoneyFull(
                    coverage.coverage_limit,
                    currency
                  )}
                </td>

                <td className="px-4 py-3">
                  {formatPercent(
                    coverage.coinsurance ?? 1
                  )}
                </td>

                <td className="px-4 py-3">
                  {formatDate(
                    coverage.expiration_date
                  )}
                </td>

                <td className="px-4 py-3">
                  {coverage.linked_scenarios_count ?? 0}
                </td>

                <td className="px-4 py-3">
                  <div className="flex items-center gap-2 whitespace-nowrap">
                     <CoverageActions
                        coverage={coverage}
                        loading={mutationLoading}
                        onManageScenarios={() =>
                          onManageScenarios(coverage.id)
                        }
                        onEdit={() =>
                          onEditCoverage(coverage)
                        }
                        onChangeStatus={(status) =>
                          onChangeStatus(
                            coverage.id,
                            status
                          )
                        }
                        onDelete={() =>
                          onDeleteCoverage(coverage)
                        }
                      />
                  </div>
                </td>

              </tr>
            );
          })}
        </tbody>
      </table>

    </div>
  );
}

function KpiCard({
  title,
  value,
}) {
  return (
    <div className="rounded-xl border border-slate-200 bg-white p-4">
      <p className="text-xs font-medium text-slate-500">
        {title}
      </p>

      <p className="mt-2 text-xl font-semibold text-slate-900">
        {value}
      </p>
    </div>
  );
}

function StatusBadge({
  status,
}) {
  const normalized =
    String(status || "UNKNOWN").toUpperCase();

  const styles = {
    ACTIVE:
      "bg-emerald-50 text-emerald-700",
    EXPIRING:
      "bg-amber-50 text-amber-700",
    EXPIRED:
      "bg-red-50 text-red-700",
    DRAFT:
      "bg-slate-100 text-slate-700",
    PROPOSED:
      "bg-blue-50 text-blue-700",
    CANCELLED:
      "bg-red-50 text-red-700",
  };

  return (
    <span
      className={`rounded-full px-2.5 py-1 text-xs font-semibold ${
        styles[normalized] ||
        "bg-slate-100 text-slate-700"
      }`}
    >
      {normalized}
    </span>
  );
}

function formatDate(value) {
  if (!value) return "—";

  const date = new Date(
    `${value}T00:00:00`
  );

  if (Number.isNaN(date.getTime())) {
    return "—";
  }

  return new Intl.DateTimeFormat("en-IE", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  }).format(date);
}

function formatCoverageType(value) {
  return String(value || "")
    .replaceAll("_", " ")
    .toLowerCase()
    .replace(/\b\w/g, (character) =>
      character.toUpperCase()
    );
}

function CoverageActions({
  coverage,
  loading,
  onManageScenarios,
  onEdit,
  onChangeStatus,
  onDelete,
}) {
  const status = String(
    coverage?.status || ""
  ).toUpperCase();

  const availableTransitions =
    getAvailableTransitions(status);

  return (
    <div className="flex flex-wrap items-center gap-2 whitespace-nowrap">
      {onManageScenarios && <ActionButton
        onClick={onManageScenarios}
        disabled={loading}
      >
        Scenarios
      </ActionButton>}

      {onEdit && <ActionButton
        onClick={onEdit}
        disabled={loading}
      >
        Edit
      </ActionButton>}

      {onChangeStatus && availableTransitions.map(
        (transition) => (
          <ActionButton
            key={transition.status}
            onClick={() =>
              onChangeStatus(
                transition.status
              )
            }
            disabled={loading}
          >
            {transition.label}
          </ActionButton>
        )
      )}

      {status === "DRAFT" && onDelete && (
        <ActionButton
          onClick={onDelete}
          disabled={loading}
          danger
        >
          Delete
        </ActionButton>
      )}
    </div>
  );
}

function ActionButton({
  children,
  onClick,
  disabled,
  danger = false,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      className={`rounded-md border px-2.5 py-1.5 text-xs font-medium disabled:cursor-not-allowed disabled:opacity-50 ${
        danger
          ? "border-red-200 text-red-700 hover:bg-red-50"
          : "border-slate-300 text-slate-700 hover:bg-slate-50"
      }`}
    >
      {children}
    </button>
  );
}

function getAvailableTransitions(status) {
  const transitions = {
    DRAFT: [
      {
        status: "PROPOSED",
        label: "Submit",
      },
      {
        status: "CANCELLED",
        label: "Cancel",
      },
    ],

    PROPOSED: [
      {
        status: "DRAFT",
        label: "Return to Draft",
      },
      {
        status: "ACTIVE",
        label: "Activate",
      },
      {
        status: "CANCELLED",
        label: "Cancel",
      },
    ],

    ACTIVE: [
      {
        status: "EXPIRING",
        label: "Mark Expiring",
      },
      {
        status: "EXPIRED",
        label: "Expire",
      },
      {
        status: "CANCELLED",
        label: "Cancel",
      },
    ],

    EXPIRING: [
      {
        status: "ACTIVE",
        label: "Reactivate",
      },
      {
        status: "EXPIRED",
        label: "Expire",
      },
      {
        status: "CANCELLED",
        label: "Cancel",
      },
    ],

    EXPIRED: [],
    CANCELLED: [],
  };

  return transitions[status] || [];
}

function DeleteCoverageDialog({
  coverage,
  loading,
  error,
  onCancel,
  onConfirm,
}) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/30 p-4">
      <div className="w-full max-w-md rounded-xl bg-white p-6 shadow-xl">
        <h2 className="text-lg font-semibold text-slate-900">
          Delete Coverage
        </h2>

        <p className="mt-2 text-sm text-slate-600">
          Delete{" "}
          <span className="font-semibold">
            {coverage.name}
          </span>
          ? This action cannot be undone.
        </p>

        {error && (
          <div className="mt-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {error}
          </div>
        )}

        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="rounded-lg border border-slate-300 px-4 py-2 text-sm text-slate-700"
          >
            Cancel
          </button>

          <button
            type="button"
            onClick={onConfirm}
            disabled={loading}
            className="rounded-lg bg-red-700 px-4 py-2 text-sm text-white disabled:opacity-50"
          >
            {loading
              ? "Deleting..."
              : "Delete Draft"}
          </button>
        </div>
      </div>
    </div>
  );
}